import { Injectable } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { Paciente, SignosVitales } from '../../pages/pacientes/pacientes';
import { ClinicaService } from './clinica.service';
import { firstValueFrom } from 'rxjs';
import { Clinica } from '../interface/interface';

(pdfMake as any).vfs = (pdfFonts as any).vfs;

interface DatosRecetaNueva {
  folio: string;
  fecha: string; // dd/MM/yyyy
  hora: string; // HH:mm

  institucion: {
    nombre: string;
    direccion: string;
    telefono: string;
    clave: string;
    logo: string | null; // dataURL, listo para { image: ... }
    redes: { nombre: string; usuario: string }[];
  };

  medico: {
    nombreCompleto: string;
    rol: string;
    cedulas: string[]; // ["Licenciatura: 1234567", ...]
  };

  paciente: {
    nombreCompleto: string;
    genero: string;
    edad: string;
    fechaNacimiento: string;
    expediente: string;
    alergias: string;
  };

  signosVitales: {
    peso: string;
    talla: string;
    frecuenciaCardiaca: string;
    frecuenciaRespiratoria: string;
    saturacionOxigeno: string;
    temperatura: string;
    presionArterial: string;
    observaciones: string;
  };

  diagnostico: string;
  indicaciones: string;
  observaciones: string;

  medicamentos: {
    numero: number;
    nombre: string;
    dosis: string;
    administracion: string;
    frecuencia: string;
    duracion: string;
  }[];

  estudios: { numero: number; nombre: string; indicaciones: string }[];

  seguimiento: {
    proximaCita: string;
    motivo: string;
    metas: string;
    objetivos: string;
  };

  config: ConfiguracionRecetaPDF; // colorPrincipal, colorTexto, mostrarFirma, etc.
}

export interface EstudioReceta {
  nombre: string;
  indicaciones?: string | null;
}

interface ExtrasReceta {
  folio?: string;
  fecha?: Date;
  observacionesSignos?: string;
  estudios?: { nombre: string; indicaciones: string }[];
  seguimiento?: {
    proximaCita?: Date | string | null;
    motivo?: string;
    metas?: string;
    objetivos?: string;
  };
}

export interface DatosRecetaPDF {
  paciente: Paciente;
  signosVitales: SignosVitales | null;
  medico: any;
  medicamentos: any[];

  receta: {
    diagnostico?: string | null;
    indicaciones?: string | null;
    observaciones?: string | null;
    estudios?: EstudioReceta[] | string | null;

    // Plan de seguimiento (opcionales)
    proximaCita?: string | null;
    motivoProximaCita?: string | null;
    metas?: string | null;
    objetivos?: string | null;
  };

  folio?: string | null;
  numeroExpediente?: string | null;
}

export interface ConfiguracionRecetaPDF {
  nombreClinica: string | null;

  direccion: string | null;

  telefono: string | null;

  cedulaProfesional: string | null;

  folio: string | null;

  whatsapp: string | null;
  facebook: string | null;
  instagram: string | null;
  paginaWeb: string | null;
  email: string | null;

  noExpediente: string | null;

  colorPrincipal: string;

  colorTexto: string;

  mostrarDireccion: boolean;

  mostrarTelefono: boolean;

  mostrarCedula: boolean;

  mostrarFolio: boolean;

  mostrarWhatsapp: boolean;
  mostrarFacebook: boolean;
  mostrarInstagram: boolean;
  mostrarPaginaWeb: boolean;
  mostrarNoEmail: boolean;
  mostrarRedes: boolean;
  mostrarNoExpediente: boolean;

  mostrarFecha: boolean;

  mostrarFirma: boolean;

  nombreFirma: string | null;

  mostrarLogo: boolean;

  mostrarMarcaAgua: boolean;

  textoMarcaAgua: string | null;

  colorMarcaAgua: string;

  opacidadMarcaAgua: number;

  anguloMarcaAgua: number;

  disposicion: 'clasico' | 'moderno' | 'nueva';
}

@Injectable({
  providedIn: 'root',
})
export class RecetasPdf {
  private readonly STORAGE_KEY = 'configuracionRecetaPDF';

  private configuracionPredeterminada: ConfiguracionRecetaPDF = {
    nombreClinica: 'Clínica NovaMed',

    direccion: 'Av. Principal #123, Oaxaca, Oaxaca',

    telefono: '951 123 4567',

    cedulaProfesional: '12345678',

    folio: 'REC-2026-0001',
    mostrarRedes: true,
    whatsapp: '',
    facebook: '',
    instagram: '',
    paginaWeb: '',
    email: '',

    noExpediente: 'JDGHJDFGHJK908',

    colorPrincipal: '#24575A',

    colorTexto: '#333333',

    mostrarDireccion: true,

    mostrarTelefono: true,

    mostrarCedula: true,

    mostrarFolio: true,

    mostrarWhatsapp: true,

    mostrarFacebook: true,

    mostrarInstagram: true,

    mostrarPaginaWeb: true,

    mostrarNoEmail: true,

    mostrarNoExpediente: true,

    mostrarFecha: true,

    mostrarFirma: true,

    nombreFirma: 'Médico responsable',

    mostrarLogo: false,

    mostrarMarcaAgua: true,

    textoMarcaAgua: 'Clínica NovaMed',

    colorMarcaAgua: '#6FB6A6',

    opacidadMarcaAgua: 0.3,

    anguloMarcaAgua: 70,

    disposicion: 'clasico',
  };

  private configuracion: ConfiguracionRecetaPDF;

  // Logo institucional: SOLO en memoria, nunca en localStorage.

  private logoBase64: string | null = null;

  private clinicaActual: Clinica | null = null;
  // generarRecetaNuevaBlob: any;

  constructor(private clinicaService: ClinicaService) {
    this.configuracion = this.cargarDesdeStorage();
  }

  private construirSeguimiento(receta: DatosRecetaPDF['receta'], espacioSeccion: number): any[] {
    const bloques: any[] = [];

    const metas = receta.metas?.trim();
    const objetivos = receta.objetivos?.trim();
    const proximaCita = receta.proximaCita?.trim();
    const motivo = receta.motivoProximaCita?.trim();

    if (proximaCita) {
      const detalle: any[] = [{ text: proximaCita, bold: true }];

      if (motivo) {
        detalle.push({ text: [{ text: 'Motivo: ', bold: true }, motivo] });
      }

      bloques.push({
        unbreakable: true,
        margin: [0, espacioSeccion, 0, 0],
        stack: [{ text: 'Próxima cita', style: 'seccion' }, ...detalle],
      });
    }

    if (metas) {
      bloques.push({
        unbreakable: true,
        margin: [0, espacioSeccion, 0, 0],
        stack: [{ text: 'Metas', style: 'seccion' }, { text: metas }],
      });
    }

    if (objetivos) {
      bloques.push({
        unbreakable: true,
        margin: [0, espacioSeccion, 0, 0],
        stack: [{ text: 'Objetivos', style: 'seccion' }, { text: objetivos }],
      });
    }

    return bloques;
  }

  // =====================================================

  // STORAGE (solo configuración de estilo/texto, sin imágenes)

  // =====================================================

  private cargarDesdeStorage(): ConfiguracionRecetaPDF {
    const guardado = localStorage.getItem(this.STORAGE_KEY);

    if (!guardado) {
      return { ...this.configuracionPredeterminada };
    }

    try {
      return {
        ...this.configuracionPredeterminada,

        ...JSON.parse(guardado),
      };
    } catch {
      return { ...this.configuracionPredeterminada };
    }
  }

  private guardarEnStorage(): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.configuracion));
  }

  configurar(configuracion: Partial<ConfiguracionRecetaPDF>): void {
    this.configuracion = {
      ...this.configuracion,

      ...configuracion,
    };

    this.guardarEnStorage();
  }

  restaurarPredeterminada(): void {
    this.configuracion = { ...this.configuracionPredeterminada };

    this.guardarEnStorage();
  }

  obtenerConfiguracion(): ConfiguracionRecetaPDF {
    return { ...this.configuracion };
  }

  // =====================================================

  // CLÍNICA (nombre y dirección reales, no localStorage)

  // =====================================================

  private async cargarClinica(): Promise<void> {
    try {
      this.clinicaActual = await firstValueFrom(this.clinicaService.obtenerClinica());
    } catch (error) {
      console.error('No se pudo cargar la información de la clínica:', error);

      this.clinicaActual = null;
    }
  }

  // =====================================================

  // LOGO INSTITUCIONAL (proviene siempre de ClinicaService)

  // =====================================================

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => resolve(reader.result as string);

      reader.onerror = () => reject(reader.error);

      reader.readAsDataURL(blob);
    });
  }

  /** Refresca el logo desde el backend. No persiste nada en localStorage. */

  private async cargarLogoClinica(): Promise<void> {
    try {
      const blob = await firstValueFrom(this.clinicaService.obtenerArchivoLogo());

      this.logoBase64 = await this.blobToBase64(blob);
    } catch (error) {
      console.error('No se pudo cargar el logo de la clínica:', error);

      this.logoBase64 = null;
    }
  }

  /** Se llama desde Configuraciones justo después de subir un logo nuevo, para refrescar la caché en memoria. */

  async establecerLogoDesdeBlob(blob: Blob): Promise<void> {
    this.logoBase64 = await this.blobToBase64(blob);
  }

  /** Último logo cargado (sin volver a pedirlo al backend). Útil para previews síncronas. */

  obtenerLogoActual(): string | null {
    return this.logoBase64;
  }

  /** Fuerza la recarga del logo desde el backend y lo regresa. Úsalo en componentes que muestren preview. */

  async obtenerLogoActualizado(): Promise<string | null> {
    await this.cargarLogoClinica();

    return this.logoBase64;
  }

  // =====================================================

  // GENERAR RECETA NORMAL

  // =====================================================

  private async crearDocumento(datos: DatosRecetaPDF): Promise<TDocumentDefinitions> {
    if (this.configuracion.disposicion === 'nueva') {
      // prepararDatosRecetaNueva ya recarga logo y clínica
      const d = await this.prepararDatosRecetaNueva(datos);
      return this.construirDocumentoNuevo(d);
    }

    await Promise.all([this.cargarLogoClinica(), this.cargarClinica()]);
    return this.construirDocumento(datos);
  }

  async generarReceta(datos: DatosRecetaPDF): Promise<void> {
    const docDefinition = await this.crearDocumento(datos);
    pdfMake.createPdf(docDefinition).open();
  }

  async generarRecetaBlob(datos: DatosRecetaPDF): Promise<Blob> {
    const docDefinition = await this.crearDocumento(datos);
    return pdfMake.createPdf(docDefinition).getBlob();
  }

  // =====================================================

  // CONSTRUIR DOCUMENTO

  // =====================================================

  private construirDocumento(datos: DatosRecetaPDF): TDocumentDefinitions {
    const config = this.configuracion;

    const logo = this.logoBase64;

    const paciente = datos.paciente;

    const signosVitales = datos.signosVitales;

    const medico = datos.medico;

    const receta = datos.receta;

    const medicamentos = datos.medicamentos;

    // Espacio vertical entre secciones (en puntos). Súbelo o bájalo para ajustar todo a la vez.

    const espacioSeccion = 6;

    const nombreClinica = this.clinicaActual?.nombre ?? config.nombreClinica;

    const direccionClinica = this.clinicaActual?.direccion ?? config.direccion;

    const folio = datos.folio ?? config.folio;

    const numeroExpediente =
      datos.numeroExpediente ?? paciente.numeroExpedienteMedico ?? config.noExpediente;

    // Estilo común de las tablas: sin bordes laterales, separadores finos y filas alternadas
    const layoutTabla: any = {
      hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 0 : 0.5),
      vLineWidth: () => 0,
      hLineColor: () => '#d9e2e1',
      fillColor: (rowIndex: number) => (rowIndex > 0 && rowIndex % 2 === 0 ? '#f3f8f7' : null),
      paddingTop: () => 5,
      paddingBottom: () => 5,
      paddingLeft: () => 6,
      paddingRight: () => 6,
    };

    // Valor con unidad; si no hay dato muestra un guion para que no se parta en varias líneas
    const dato = (valor: any, unidad = ''): string => {
      const texto = `${valor ?? ''}`.trim();
      return texto ? `${texto}${unidad}` : '—';
    };
    // ===================================================

    // MÉDICO

    // ===================================================

    const nombreMedico = `

      ${medico?.nombre || ''}

      ${medico?.apellidoPaterno || ''}

      ${medico?.apellidoMaterno || ''}

    `

      .replace(/\s+/g, ' ')

      .trim();

    // ===================================================

    // PACIENTE

    // ===================================================

    const nombrePaciente = `

      ${paciente.nombre || ''}

      ${paciente.apellido || ''}

      ${paciente.segundoApellido || ''}

    `

      .replace(/\s+/g, ' ')

      .trim();

    // ===================================================

    // EDAD

    // ===================================================

    const edad = this.obtenerEdad(paciente);

    // ===================================================

    // SEXO

    // ===================================================

    const sexo = `${paciente.genero}`.replace(/\s+/g, ' ').trim();

    // ===================================================

    // ALERGIAS

    // ===================================================

    const alergias =
      `${paciente.expediente?.alergias ?? ''}`.replace(/\s+/g, ' ').trim() || 'No registradas';
    // ===================================================

    // FECHA

    // ===================================================

    const fecha = new Date().toLocaleDateString('es-MX', {
      day: '2-digit',

      month: '2-digit',

      year: 'numeric',
    });

    // ===================================================

    // MEDICAMENTOS

    // ===================================================

    const valorTabla = (valor: any): string => {
      const texto = `${valor ?? ''}`.trim();
      return texto || '—';
    };

    const medicamentosBody = medicamentos.map((med, index) => [
      {
        text: `${index + 1}. ${med.medicamento || 'No especificado'}`,
        bold: true,
      },

      { text: valorTabla(med.dosis), alignment: 'center' },

      { text: valorTabla(med.administracion), alignment: 'center' },

      { text: valorTabla(med.frecuencia), alignment: 'center' },

      { text: valorTabla(med.duracion), alignment: 'center' },
    ]);
    // ===================================================

    // ENCABEZADO

    // ===================================================

    let columnas: any[];

    // Segunda fila del encabezado (solo disposición clásica):

    // folio a la izquierda y expediente + fecha a la derecha,

    // ambos al mismo nivel y con la misma separación.

    let filaDatosClasica: any = null;

    if (config.disposicion === 'moderno') {
      const stackModerno: any[] = [];

      if (config.mostrarLogo && logo) {
        stackModerno.push({
          image: logo,

          width: 60,

          height: 60,

          alignment: 'center',

          margin: [0, 0, 0, 10],
        });
      }

      stackModerno.push({
        text: nombreClinica,

        style: 'clinicaNombre',

        alignment: 'center',
      });

      if (config.mostrarDireccion) {
        stackModerno.push({
          text: direccionClinica,

          style: 'clinicaInfo',

          alignment: 'center',
        });
      }

      if (config.mostrarTelefono) {
        stackModerno.push({
          text: `Tel. ${config.telefono}`,

          style: 'clinicaInfo',

          alignment: 'center',
        });
      }

      stackModerno.push({
        text: `Médico responsable: ${nombreMedico || 'No registrado'}`,

        style: 'tituloReceta',

        alignment: 'center',

        margin: [0, 10, 0, 0],
      });

      const datosMedicoModerno: string[] = [];

      if (config.mostrarCedula) {
        datosMedicoModerno.push(`Cédula: ${config.cedulaProfesional}`);
      }

      if (config.mostrarFolio) {
        datosMedicoModerno.push(`Folio: ${folio}`);
      }

      if (config.mostrarNoExpediente) {
        datosMedicoModerno.push(`Exp: ${numeroExpediente}`);
      }

      if (config.mostrarFecha) {
        datosMedicoModerno.push(fecha);
      }

      if (datosMedicoModerno.length > 0) {
        stackModerno.push({
          text: datosMedicoModerno.join('   |   '),

          style: 'folio',

          alignment: 'center',

          margin: [0, 5, 0, 0],
        });
      }

      columnas = [
        {
          width: '*',

          stack: stackModerno,
        },
      ];
    } else {
      // ==========================================

      // DISPOSICIÓN CLÁSICA

      // ==========================================

      const hayLogo = config.mostrarLogo && !!logo;

      // -------- Fila 1: clínica (izquierda) y médico (derecha) --------

      columnas = [];

      if (hayLogo) {
        columnas.push({
          width: 60,

          stack: [
            {
              image: logo,

              width: 50,

              height: 50,
            },
          ],
        });
      }

      // Columna de información de la clínica: nombre, dirección y teléfono

      const stackClinica: any[] = [
        {
          text: nombreClinica,

          style: 'clinicaNombre',
        },
      ];

      if (config.mostrarDireccion) {
        stackClinica.push({
          text: direccionClinica,

          style: 'clinicaInfo',
        });
      }

      if (config.mostrarTelefono) {
        stackClinica.push({
          text: `Tel. ${config.telefono}`,

          style: 'clinicaInfo',
        });
      }

      columnas.push({
        width: '*',

        stack: stackClinica,
      });

      // Columna del médico: nombre y cédula profesional

      const stackMedico: any[] = [
        {
          text: '',

          style: 'tituloReceta',

          alignment: 'right',
        },

        {
          text: nombreMedico || 'No registrado',

          style: 'nombreMedicoClasico',

          alignment: 'right',

          fit: 'shrink',
        },
      ];

      if (config.mostrarCedula) {
        stackMedico.push({
          text: `Cédula profesional: ${config.cedulaProfesional}`,

          style: 'dato',

          alignment: 'right',
        });
      }

      columnas.push({
        width: '40%',

        stack: stackMedico,
      });

      // -------- Fila 2: folio (debajo del teléfono) y expediente + fecha

      //          (debajo de la cédula), al mismo nivel --------

      const stackFolio: any[] = [];

      if (config.mostrarFolio) {
        stackFolio.push({
          text: `Folio: ${folio}`,

          style: 'folioClasico',
        });
      }

      const stackExpedienteFecha: any[] = [];

      if (config.mostrarNoExpediente) {
        stackExpedienteFecha.push({
          text: `Exp. n.º: ${numeroExpediente}`,

          style: 'datosRecetaClasico',
        });
      }

      if (config.mostrarFecha) {
        stackExpedienteFecha.push({
          text: fecha,

          style: 'datosRecetaClasico',

          margin: [0, -1, 0, 0],
        });
      }

      if (stackFolio.length > 0 || stackExpedienteFecha.length > 0) {
        const columnasDatos: any[] = [];

        // Espacio equivalente al logo para que el folio quede alineado con el texto de la clínica

        if (hayLogo) {
          columnasDatos.push({ width: 60, text: '' });
        }

        columnasDatos.push({
          width: '*',

          stack: stackFolio,
        });

        columnasDatos.push({
          width: '40%',

          stack: stackExpedienteFecha,
        });

        filaDatosClasica = {
          columns: columnasDatos,

          // Separación respecto al teléfono y la cédula (ajusta este valor a tu gusto)

          margin: [0, 5, 0, 0],
        };
      }
    }

    const encabezado: any = filaDatosClasica
      ? { stack: [{ columns: columnas }, filaDatosClasica] }
      : { columns: columnas };

    // ===================================================

    // CONTENIDO

    // ===================================================

    // Frecuencia respiratoria: cuando el backend la envíe, solo cambia el nombre del campo si es distinto
    const frecuenciaRespiratoria = (signosVitales as any)?.frecuenciaRespiratoria;

    const presionArterial =
      signosVitales?.presionSistolica !== null &&
      signosVitales?.presionSistolica !== undefined &&
      signosVitales?.presionDiastolica !== null &&
      signosVitales?.presionDiastolica !== undefined
        ? `${signosVitales.presionSistolica}/${signosVitales.presionDiastolica} mmHg`
        : '—';

    const columnasSignos = [
      { titulo: 'Peso', valor: dato(signosVitales?.peso, ' kg'), ancho: '12%' },
      { titulo: 'Talla', valor: dato(signosVitales?.estatura, ' m'), ancho: '12%' },
      { titulo: 'FC', valor: dato(signosVitales?.frecuenciaCardiaca, ' lpm'), ancho: '13%' },
      { titulo: 'SatO2', valor: dato(signosVitales?.saturacionOxigeno, ' %'), ancho: '13%' },
      { titulo: 'FR', valor: dato(frecuenciaRespiratoria, ' rpm'), ancho: '13%' },
      { titulo: 'Temperatura', valor: dato(signosVitales?.temperatura, ' °C'), ancho: '17%' },
      { titulo: 'TA', valor: presionArterial, ancho: '20%' },
    ];

    const contenido: any[] = [
      encabezado,

      {
        canvas: [
          {
            type: 'line',

            x1: 0,

            y1: 8,

            x2: 515,

            y2: 8,

            lineWidth: 1,
          },
        ],

        margin: [0, 2, 0, espacioSeccion],
      },

      // -----------------------------------------------

      // PACIENTE

      // -----------------------------------------------

      {
        unbreakable: true,
        table: {
          widths: ['50%', '50%'],

          body: [
            [
              {
                text: [
                  {
                    text: 'Paciente: ',

                    bold: true,
                  },

                  nombrePaciente,
                ],

                style: 'datoPaciente',
              },

              {
                text: [
                  {
                    text: 'Edad: ',

                    bold: true,
                  },

                  `${edad} años`,
                ],

                style: 'datoPaciente',
              },
            ],

            [
              {
                text: [
                  {
                    text: 'Sexo: ',

                    bold: true,
                  },

                  sexo,
                ],

                style: 'datoPaciente',
              },

              {
                text: [
                  {
                    text: 'Alergias: ',

                    bold: true,
                  },

                  alergias,
                ],

                style: 'datoPaciente',
              },
            ],
          ],
        },

        layout: 'noBorders',

        margin: [0, 0, 0, espacioSeccion],
      },

      // -----------------------------------------------
      // SIGNOS VITALES
      // -----------------------------------------------

      {
        unbreakable: true,
        fontSize: 10,

        table: {
          headerRows: 1,
          widths: columnasSignos.map((c) => c.ancho),

          body: [
            columnasSignos.map((c) => ({ text: c.titulo, style: 'encabezadoTabla' })),
            columnasSignos.map((c) => ({ text: c.valor, alignment: 'center' })),
          ],
        },

        layout: layoutTabla,

        margin: [0, 0, 0, espacioSeccion],
      },

      // -----------------------------------------------

      // DIAGNÓSTICO

      // -----------------------------------------------

      {
        // Mantiene el título junto con su contenido.
        // Si no cabe al final de la página, pasa completo a la siguiente.
        unbreakable: true,
        stack: [
          {
            text: 'Diagnóstico',
            style: 'seccion',
          },
          {
            text: receta.diagnostico || 'No especificado',
            style: 'diagnostico',
            margin: [0, 0, 0, espacioSeccion],
          },
        ],
      },
    ];

    // ===================================================

    // TABLA DE MEDICAMENTOS

    // ===================================================

    if (medicamentos.length > 0) {
      contenido.push({
        fontSize: 11,

        table: {
          headerRows: 1,
          keepWithHeaderRows: 1, // el encabezado nunca queda solo al final de una página
          dontBreakRows: true, // una fila no se corta entre dos páginas

          widths: ['35%', '20%', '20%', '14%', '10%'],

          body: [
            [
              { text: 'Medicamento', style: 'encabezadoTabla', alignment: 'left' },
              { text: 'Dosis', style: 'encabezadoTabla' },
              { text: 'Administración', style: 'encabezadoTabla' },
              { text: 'Indicaciones', style: 'encabezadoTabla' },

              { text: 'Duración', style: 'encabezadoTabla' },
            ],

            ...medicamentosBody,
          ],
        },

        layout: layoutTabla,

        margin: [0, 0, 0, espacioSeccion],
      });
    } else {
      contenido.push({
        text: 'No se agregaron medicamentos.',

        italics: true,

        margin: [0, 0, 0, espacioSeccion],
      });
    }

    // ===================================================

    // INDICACIONES

    // ===================================================

    contenido.push({
      unbreakable: true,
      stack: [
        { text: 'Indicaciones', style: 'seccion' },
        {
          text: receta.indicaciones || 'No se registraron indicaciones.',
          margin: [0, 0, 0, espacioSeccion],
        },
      ],
    });

    // ===================================================

    // OBSERVACIONES

    // ===================================================

    contenido.push({
      unbreakable: true,
      stack: [
        { text: 'Observaciones', style: 'seccion' },
        {
          text: receta.observaciones || 'No se registraron observaciones.',
          margin: [0, 0, 0, espacioSeccion],
        },
      ],
    });

    // ===================================================

    // ESTUDIOS

    // ===================================================

    const estudios = this.normalizarEstudios(datos.receta.estudios);

    contenido.push({
      unbreakable: true,
      stack: [
        { text: 'Estudios solicitados', style: 'seccion' },
        ...(estudios.length
          ? estudios.map((e, i) => ({
              text: `${i + 1}. ${e.nombre}${e.indicaciones ? ` - ${e.indicaciones}` : ''}`,
            }))
          : [{ text: 'No se solicitaron estudios.' }]),
      ],
    });

    // ===================================================
    // PLAN DE SEGUIMIENTO (solo si hay algo capturado)
    // ===================================================

    contenido.push(...this.construirSeguimiento(receta, espacioSeccion));

    // ===================================================
    // redes sociales
    // ===================================================

    // ===================================================

    // DOCUMENTO

    // ===================================================

    const docDefinition: TDocumentDefinitions = {
      pageSize: 'LETTER',

      defaultStyle: {
        color: config.colorTexto,

        lineHeight: 1.05,
      },

      // =================================================

      // IMAGEN DE MARCA DE AGUA

      // =================================================

      background:
        config.mostrarMarcaAgua && logo
          ? {
              image: logo,

              width: 300,

              opacity: config.opacidadMarcaAgua,

              absolutePosition: { x: 148, y: 350 },
            }
          : undefined,

      // =================================================

      // MARCA DE AGUA DE TEXTO

      // =================================================

      watermark:
        config.mostrarMarcaAgua && !logo
          ? {
              text: config.textoMarcaAgua ?? nombreClinica ?? '',

              color: config.colorMarcaAgua,

              angle: config.anguloMarcaAgua,

              opacity: config.opacidadMarcaAgua,
            }
          : undefined,

      pageMargins: [42.52, 56.69, 42.52, 56.69],

      content: contenido,

      // =================================================

      // FIRMA

      // =================================================

      footer: (currentPage: number, pageCount: number): any => {
        const elementos: any[] = [
          {
            text: `Página ${currentPage} de ${pageCount}`,
            fontSize: 8,
            color: '#777777',
            alignment: 'left',
          },
        ];

        if (currentPage === pageCount && config.mostrarFirma) {
          elementos.push({
            text: '\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_\_',
            alignment: 'center',
            margin: [0, -25, 0, 0],
          });

          elementos.push({
            text: nombreMedico || config.nombreFirma || '',
            alignment: 'center',
            bold: true,
            fontSize: 10,
          });

          elementos.push({
            text: 'Firma del médico',
            alignment: 'center',
            fontSize: 9,
          });
        }

        // Redes sociales: siempre al final, debajo de la firma
        const redes = this.textoRedesClinica();
        if (config.mostrarRedes && redes.length) {
          elementos.push({
            text: redes,
            fontSize: 8,
            color: '#555555',
            alignment: 'center',
            margin: [0, 6, 0, 0],
          });
        }

        return {
          columns: [{ width: '*', stack: elementos }],
          margin: [20, 10, 40, 20],
        };
      },

      // =================================================

      // ESTILOS

      // =================================================

      styles: {
        nombreMedicoClasico: {
          fontSize: 15,

          bold: true,

          color: 'black',

          lineHeight: 1.05,
        },

        // Folio (disposición clásica): la separación la define la fila del encabezado

        folioClasico: {
          fontSize: 11,

          bold: true,

          color: 'red',
        },

        // Expediente y fecha (disposición clásica)

        datosRecetaClasico: {
          fontSize: 11,

          bold: true,

          color: 'red',

          alignment: 'right',
        },

        clinicaNombre: {
          fontSize: 20,

          bold: true,

          color: config.colorPrincipal,
        },

        clinicaInfo: {
          fontSize: 11,

          color: '#555555',

          margin: [0, 5, 0, 0],
        },

        listaDatos: {
          fontSize: 11,

          color: config.colorTexto,
        },

        tituloReceta: {
          fontSize: 16,

          bold: true,

          alignment: config.disposicion === 'moderno' ? 'center' : 'right',

          color: config.colorPrincipal,
        },

        folio: {
          fontSize: 11,

          alignment: config.disposicion === 'moderno' ? 'center' : 'right',

          color: '#ff0000',

          margin: [0, 1, 0, 0],
        },

        seccion: {
          fontSize: 12,

          bold: true,

          color: config.colorPrincipal,

          margin: [0, 0, 0, 2],
        },

        dato: {
          alignment: 'right',

          fontSize: 11,

          bold: true,
        },

        diagnostico: {
          fontSize: 11,
        },

        encabezadoTabla: {
          bold: true,

          color: '#FFFFFF',

          fillColor: config.colorPrincipal,

          alignment: 'center',
        },
      },
    };

    return docDefinition;
  }

  // =====================================================

  // EDAD

  // =====================================================

  private obtenerEdad(paciente: Paciente): number {
    if (!paciente.fechaNacimiento) {
      return 0;
    }

    const nacimiento = new Date(paciente.fechaNacimiento);

    const hoy = new Date();

    let edad = hoy.getFullYear() - nacimiento.getFullYear();

    const mes = hoy.getMonth() - nacimiento.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }

    return edad;
  }

  // ! ========================================
  // ! PRUEBA DE NUEVO FORMATO DE RECETA
  // ! ========================================

  private normalizarEstudios(
    estudios: EstudioReceta[] | string | null | undefined,
  ): { nombre: string; indicaciones: string }[] {
    // Formato del backend: [{ nombre, indicaciones }]
    if (Array.isArray(estudios)) {
      return estudios
        .map((e) => ({
          nombre: (e?.nombre ?? '').trim(),
          indicaciones: (e?.indicaciones ?? '').trim(),
        }))
        .filter((e) => e.nombre);
    }

    // Compatibilidad con el texto "Estudio - indicación" por línea
    return this.lineas(estudios).map((linea) => {
      const [nombre, ...resto] = linea.split(' - ');
      return { nombre: nombre.trim(), indicaciones: resto.join(' - ').trim() };
    });
  }

  private lineas(texto: string | null | undefined): string[] {
    if (!texto || texto.trim() === '—') return [];

    return texto
      .split('\n')
      .map((linea) => linea.replace(/^\s*(?:[•\-*]|\d+[.)])\s*/, '').trim()) // quita viñetas o "1." escritos a mano
      .filter(Boolean);
  }

  private texto(valor: unknown): string {
    const t = valor === null || valor === undefined ? '' : String(valor).trim();
    return t || '—';
  }

  private conUnidad(valor: unknown, unidad: string): string {
    const t = valor === null || valor === undefined ? '' : String(valor).trim();
    return t ? `${t} ${unidad}` : '—';
  }

  private formatearFecha(valor: Date | string | null | undefined): string {
    if (!valor) return '—';

    if (valor instanceof Date) {
      return valor.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }

    const texto = valor.trim();
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(texto);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;

    return texto || '—'; // ya venía formateada
  }

  async prepararDatosRecetaNueva(datos: DatosRecetaPDF): Promise<DatosRecetaNueva> {
    await Promise.all([this.cargarLogoClinica(), this.cargarClinica()]);

    const config = this.configuracion;
    const p = datos.paciente;
    const sv: any = datos.signosVitales ?? {};
    const r = datos.receta ?? {};
    const m = datos.medico ?? {};
    const ahora = new Date();

    const presion =
      sv.presionSistolica != null && sv.presionDiastolica != null
        ? `${sv.presionSistolica}/${sv.presionDiastolica} mmHg`
        : '—';

    // Si el objeto médico trae sus cédulas se usan; si no, la de la configuración
    const cedulas: string[] = Array.isArray(m.cedulas)
      ? m.cedulas.map((c: any) => `${c.tipoCedula}: ${c.cedula}`)
      : config.cedulaProfesional
        ? [config.cedulaProfesional]
        : [];

    return {
      folio: this.texto(datos.folio ?? config.folio),
      fecha: this.formatearFecha(ahora),
      hora: ahora.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }),

      institucion: {
        nombre: this.texto(this.clinicaActual?.nombre ?? config.nombreClinica),
        direccion: this.texto(this.clinicaActual?.direccion ?? config.direccion),
        clave: this.texto(this.clinicaActual?.claveClinica),
        telefono: this.texto(config.telefono),
        logo: this.logoBase64,
        redes: (this.clinicaActual?.redesSociales ?? [])
          .filter((r) => r.redSocial?.trim() && r.usuario?.trim())
          .map((r) => ({ nombre: r.redSocial.trim(), usuario: r.usuario.trim() })),
      },

      medico: {
        nombreCompleto: this.texto(
          [m.nombre, m.apellidoPaterno, m.apellidoMaterno].filter(Boolean).join(' '),
        ),
        rol: this.texto(m.rol),
        cedulas,
      },

      paciente: {
        nombreCompleto: this.texto(
          [p.nombre, p.apellido, p.segundoApellido].filter(Boolean).join(' '),
        ),
        genero: this.texto(p.genero),
        edad: `${this.obtenerEdad(p)} años`,
        fechaNacimiento: this.formatearFecha(p.fechaNacimiento as any),
        expediente: this.texto(
          datos.numeroExpediente ?? p.numeroExpedienteMedico ?? config.noExpediente,
        ),
        alergias: this.texto(p.expediente?.alergias || 'No registradas'),
      },

      signosVitales: {
        peso: this.conUnidad(sv.peso, 'kg'),
        talla: this.conUnidad(sv.estatura, 'm'),
        frecuenciaCardiaca: this.conUnidad(sv.frecuenciaCardiaca, 'lpm'),
        frecuenciaRespiratoria: this.conUnidad(sv.frecuenciaRespiratoria, 'rpm'),
        saturacionOxigeno: this.conUnidad(sv.saturacionOxigeno, '%'),
        temperatura: this.conUnidad(sv.temperatura, '°C'),
        presionArterial: presion,
        observaciones: this.texto(
          sv.observaciones ?? (datos.receta as any)?.observacionesSignosVitales,
        ),
      },

      diagnostico: this.texto(r.diagnostico),
      indicaciones: this.texto(r.indicaciones),
      observaciones: this.texto(r.observaciones),

      medicamentos: (datos.medicamentos ?? []).map((med: any, i: number) => ({
        numero: i + 1,
        nombre: this.texto(med.medicamento),
        dosis: this.texto(med.dosis),
        administracion: this.texto(med.administracion),
        frecuencia: this.texto(med.frecuencia),

        duracion: this.texto(med.duracion),
      })),

      estudios: this.normalizarEstudios(r.estudios).map((e, i) => ({
        numero: i + 1,
        nombre: e.nombre,
        indicaciones: e.indicaciones || '—',
      })),

      seguimiento: {
        proximaCita: this.formatearFecha(r.proximaCita),
        motivo: this.texto(r.motivoProximaCita),
        metas: this.texto(r.metas),
        objetivos: this.texto(r.objetivos),
      },

      config,
    };
  }

  private tablaSignosVitales(d: DatosRecetaNueva): any {
    const sv = d.signosVitales;
    const color = d.config.colorPrincipal;

    const columnas = [
      { titulo: 'Peso', valor: sv.peso, ancho: '11%' },
      { titulo: 'Talla', valor: sv.talla, ancho: '11%' },
      { titulo: 'FC', valor: sv.frecuenciaCardiaca, ancho: '13%' },
      { titulo: 'FR', valor: sv.frecuenciaRespiratoria, ancho: '13%' },
      { titulo: 'SatO2', valor: sv.saturacionOxigeno, ancho: '11%' },
      { titulo: 'Temp.', valor: sv.temperatura, ancho: '14%' },
      { titulo: 'TA', valor: sv.presionArterial, ancho: '27%' },
    ];

    const filas: any[] = [
      // Encabezado con el color principal
      columnas.map((c) => ({
        text: c.titulo,
        bold: true,
        color: '#FFFFFF',
        fillColor: color,
        alignment: 'center',
        fontSize: 10,
        margin: [0, 2, 0, 2],
      })),
      // Valores
      columnas.map((c) => ({
        text: c.valor,
        bold: true,
        alignment: 'center',
        fontSize: 10,
        margin: [0, 2, 0, 2],
      })),
    ];

    // Fila de observaciones, solo si hay
    // if (sv.observaciones && sv.observaciones !== '—') {
    //   filas.push([
    //     {
    //       colSpan: 7,
    //       fontSize: 9,
    //       fillColor: '#f3f8f7',
    //       margin: [4, 3, 4, 3],
    //       text: [{ text: 'Observaciones: ', bold: true, color }, { text: sv.observaciones }],
    //     },
    //     {},
    //     {},
    //     {},
    //     {},
    //     {},
    //     {},
    //   ]);
    // }

    return {
      unbreakable: true,
      table: {
        headerRows: 1,
        widths: columnas.map((c) => c.ancho),
        body: filas,
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => '#c9d6d5',
        vLineColor: () => '#c9d6d5',
        paddingTop: () => 2,
        paddingBottom: () => 2,
      },
    };
  }

  private textoRedesClinica(): any[] {
    const redes = (this.clinicaActual?.redesSociales ?? []).filter(
      (r) => r.redSocial?.trim() && r.usuario?.trim(),
    );
    const partes: any[] = [];
    redes.forEach((r, i) => {
      if (i > 0) partes.push({ text: '   |   ', color: '#9AA8A8' });
      partes.push({ text: `${r.redSocial}: `, bold: true });
      partes.push({ text: r.usuario });
    });
    return partes;
  }

  private pieRedes(d: DatosRecetaNueva): any {
    const redes = d.institucion.redes;

    if (!d.config.mostrarRedes || redes.length === 0) return { text: '' };

    const partes: any[] = [];

    redes.forEach((r, i) => {
      if (i > 0) partes.push({ text: '   |   ', color: '#9AA8A8' });

      partes.push({ text: `${r.nombre}: `, bold: true, color: d.config.colorPrincipal });

      const esUrl = /^https?:\/\//i.test(r.usuario);
      partes.push(
        esUrl
          ? { text: r.usuario, link: r.usuario, color: '#1a5fb4', decoration: 'underline' }
          : { text: r.usuario },
      );
    });

    return {
      text: partes,
      alignment: 'center',
      fontSize: 9,
      margin: [42.52, 10, 42.52, 0],
    };
  }

  private construirDocumentoNuevo(d: DatosRecetaNueva): TDocumentDefinitions {
    const listaIndicaciones = this.lineas(d.indicaciones);
    const listaObservaciones = this.lineas(d.observaciones);
    const CM = 28.3465; // 1 cm en puntos
    const ALTO_LOGO = 1.5 * CM; // ≈ 42.52 pt
    return {
      pageSize: 'LETTER',
      pageMargins: [28.35, 28.35, 28.35, 60],
      defaultStyle: { color: d.config.colorTexto, lineHeight: 1.05 },
      footer: (paginaActual: number, totalPaginas: number) => ({
        stack: [
          this.pieRedes(d),
          {
            text: `Página ${paginaActual} de ${totalPaginas}`,
            alignment: 'center',
            fontSize: 8,
            color: '#777777',
            margin: [0, 4, 0, 0],
          },
        ],
      }),
      content: [
        {
          // ! ENCABEZADO
          columns: [
            ...(d.institucion.logo
              ? [
                  {
                    image: d.institucion.logo,
                    fit: [150, ALTO_LOGO] as [number, number], // máx. 150 pt de ancho, 1.5 cm de alto
                    width: 'auto' as const,
                    alignment: 'left' as const,
                  },
                ]
              : []),
            {
              width: '*',
              text: [
                { text: `${d.fecha}\n`, color: '#24575A', bold: true },
                { text: d.paciente.expediente, color: '#E67E22', bold: true },
              ],
              alignment: 'right',
              color: 'black',
            },
          ],
          columnGap: 10,
        },

        // ! FOLIIO
        {
          text: d.folio,
          fontSize: 11,
          bold: true,
          color: 'red',
          alignment: 'center',
        },

        // ! PACIENTE
        {
          columns: [
            {
              color: 'black',
              text: `Paciente: ${d.paciente.nombreCompleto} \n Edad: ${d.paciente.edad}`,
            },
            {
              text: [
                { text: `Genero: ${d.paciente.genero}\n` },
                { text: `Alergias: ` },
                { color: 'red', text: `${d.paciente.alergias}` },
              ],
              margin: [110, 0, 0, 0],
              color: 'black',
            },
          ],
        },

        // ! SIGNOS VITALES
        {
          // text: 'SIGNOS VITALES',
          // bold: true,
          // color: d.config.colorPrincipal,
          // fontSize: 11,
          // margin: [0, 10, 0, 3],
        },
        this.tablaSignosVitales(d),
        // ! DIAGNOSTICO
        {
          text: d.diagnostico,
          fontSize: 11,
          bold: true,
          color: 'black',
          alignment: 'center',
          margin: [0, 10, 0, 0],
        },

        // ! MEDICAMENTOS
        {
          text: 'MEDICAMENTOS',
          bold: true,
          color: d.config.colorPrincipal,
          fontSize: 11,
          alignment: 'left',
          margin: [0, 10, 0, 0],
        },
        ...(d.medicamentos.length
          ? d.medicamentos.map((med) => ({
              stack: [
                {
                  text: `${med.numero}. ${med.nombre} ${med.dosis}. ${med.administracion}. ${med.frecuencia} por ${med.duracion}`,
                  // bold: true,
                  lineHeight: 1.0,
                  fontSize: 11,
                },
              ],
            }))
          : [{ text: 'No se agregaron medicamentos.' }]),

        // ! INDICACIONES DE LA RECETA
        {
          text: 'INDICACIONES',
          bold: true,
          color: d.config.colorPrincipal,
          fontSize: 11,
          margin: [0, 10, 0, 0],
        },
        ...(listaIndicaciones.length
          ? listaIndicaciones.map((texto, i) => ({
              text: [{ text: `${i + 1}. ` }, { text: texto }],
              fontSize: 11,
              lineHeight: 1.0,
              margin: [0, 0, 0, 0],
            }))
          : [{ text: 'No se registraron indicaciones.', fontSize: 11 }]),

        // ! OBSERVACIONES DE LA RECETA
        {
          text: 'OBSERVACIONES',
          bold: true,
          color: d.config.colorPrincipal,
          fontSize: 11,
          margin: [0, 10, 0, 0],
        },
        ...(listaObservaciones.length
          ? listaObservaciones.map((texto) => ({
              text: [{ text: '• ', bold: true, color: d.config.colorPrincipal }, { text: texto }],
              fontSize: 11,
              lineHeight: 1.0,
              margin: [0, 2, 0, 0],
            }))
          : [{ text: 'No se registraron observaciones.', fontSize: 11 }]),

        // ! ESTUDIOS SOLICITADOS
        {
          margin: [0, 5, 0, 0],
          text: 'ESTUDIOS SOLICITADOS',
          bold: true,
          fontSize: 11,

          color: d.config.colorPrincipal,
        },
        ...(d.estudios.length
          ? d.estudios.map((e) => ({
              text: [
                { text: `${e.numero}. ${e.nombre}` },
                ...(e.indicaciones !== '—' ? [{ text: `  (${e.indicaciones})` }] : []),
              ],
              fontSize: 11,
              lineHeight: 1.0,
              margin: [0, 2, 0, 0],
            }))
          : [{ text: 'No se solicitaron estudios.', fontSize: 11 }]),

        // ! PROXIMA CONSULTA
        {
          text: 'SEGUIMIENTO',
          fontSize: 11,
          bold: true,
          color: d.config.colorPrincipal,
          margin: [0, 5, 0, 5],
        },

        {
          table: {
            margin: [0, 5, 0, 0],
            widths: [70, '*'],
            body: [
              [
                {
                  fillColor: d.config.colorPrincipal,
                  color: 'white',
                  fontSize: 11,
                  text: 'Próxima cita',
                  bold: true,
                },
                { fontSize: 11, text: d.seguimiento.proximaCita ?? 'No especificada' },
              ],
              [
                { fontSize: 11, text: 'Motivo', bold: true },
                { fontSize: 11, text: d.seguimiento.motivo ?? 'No especificado' },
              ],
              [
                {
                  fillColor: d.config.colorPrincipal,
                  color: 'white',
                  fontSize: 11,
                  text: 'Metas',
                  bold: true,
                },
                { fontSize: 11, text: d.seguimiento.metas ?? 'No especificadas' },
              ],
              [
                { fontSize: 11, text: 'Objetivos', bold: true },
                { fontSize: 11, text: d.seguimiento.objetivos ?? 'No especificados' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
        },

        {
          text: `${d.medico.nombreCompleto}\n CED PROF: ${d.medico.cedulas}`,
          fontSize: 11,
          bold: true,
          color: 'black',
          alignment: 'center',
          margin: [0, 40, 0, 0],
        },
      ],
    };
  }

  async generarRecetaNuevaBlob(datos: DatosRecetaPDF): Promise<Blob> {
    const d = await this.prepararDatosRecetaNueva(datos);
    return pdfMake.createPdf(this.construirDocumentoNuevo(d)).getBlob();
  }
}
